import { Trans, useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { Center, Flex, HStack, Text, useToast } from '@chakra-ui/react';
import {
  createNewOffer,
  CreateNewOfferProps,
  postConfigData,
  PromoCodeResult,
} from 'api';
import RoundedBox from 'ui/molecules/RoundedBox';
import Button from 'ui/molecules/Button';
import { SummaryFormIcon } from 'ui/icons/Icons';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { AnswersSelector, StepParamsSelector } from 'store/selectors';
import { useEffect, useMemo } from 'react';
import getEstimateUrl from 'utils/getEstimateUrl';
import tagging from 'features/tagging/tagging';
import { getCurrentVendor, isRosselVersion } from 'utils/vendorUtils';
import 'react-phone-number-input/style.css';
import './phoneNumberInputStyles.css';
import {
  allConditionalSteps,
  getStepNamesAccordingToChoices,
} from 'config/steps';
import AppRoutes from 'config/AppRoutes';
import { getClientDetails } from 'utils/getClientDetails';
import ContactForm from 'components/ContactForm/ContactForm';
import SummaryExtraServices from 'components/SummaryExtraServices/SummaryExtraServices';
import { FormContactSMESchema } from 'components/SMEContactForm/hooks/useFormContactSMESchema';
import { FormContactSchema } from 'components/ContactForm/hooks/useFormContactSchema';
import SMEContactForm from 'components/SMEContactForm/SMEContactForm';
import { notifySentry } from 'utils/error/notifySentry';
import PromoCodeForm from '../PromoCodeForm';
import { useVendorConfig } from '../../store/vendor/useVendorConfig';

type Props = {
  displayPrice: number;
  promoCodeDetails?: PromoCodeResult;
  offerDetail: CreateNewOfferProps['offerDetail'];
  simulatorData: CreateNewOfferProps['simulatorData'];
  preview?: boolean;
  numPanels: number;
  setPromoCodeDetails: (promoCodeDetails: PromoCodeResult) => void;
  onSuccess: (condigID: string) => void;
  formTitleRef: React.RefObject<HTMLDivElement>;
};

const SummaryForm = ({
  formTitleRef,
  displayPrice,
  promoCodeDetails,
  offerDetail,
  simulatorData,
  preview,
  numPanels,
  setPromoCodeDetails,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const vendorConfig = useVendorConfig();
  const toast = useToast();
  const answers = useSelector(AnswersSelector);
  const createOfferMutation = useMutation(createNewOffer);
  const createConfigMutation = useMutation(postConfigData);
  const stepParams = useSelector(StepParamsSelector);
  const steps = useMemo(
    () => getStepNamesAccordingToChoices(allConditionalSteps, stepParams),
    [stepParams]
  );

  // add context for sentry
  Sentry.setContext('offerDetail', offerDetail);
  Sentry.setContext('simulatorData', simulatorData);

  const isB2B = answers.businessChoice === 'b2b';
  const apiError = createOfferMutation.error || createConfigMutation.error;
  const isSubmitting =
    createOfferMutation.isLoading || createConfigMutation.isLoading;

  // --------------------- handlers ------------------------

  const getRequestDescription = (configUrl: string) => {
    const detailUrl = `Url de config: [${configUrl}`;
    const simulationType = `Simulation de type: [${answers.flowChoice}]`;
    const extraServices = `Extra services Request: [${answers.extraServices
      .map((service) => service.label)
      .join(', ')}]`;
    return [detailUrl, simulationType, extraServices].join(' - ');
  };

  const submitForm = async (data: FormContactSchema & FormContactSMESchema) => {
    // STEP 1 --> Create/Save configuration
    createConfigMutation.mutate(
      { answers, promoCodeDetails, vendor: getCurrentVendor() },
      {
        onSuccess: (createConfigResult) => {
          const { configId } = createConfigResult.data;
          localStorage?.setItem('lastConfigID', configId);
          const configUrl = getEstimateUrl(configId, false);
          console.log('configUrl: ', configUrl);

          const offerProps: CreateNewOfferProps = {
            configId,
            promoCode: promoCodeDetails?.code,
            totalPrice: displayPrice,
            clientDetail: getClientDetails(
              data,
              answers,
              vendorConfig,
              getRequestDescription(configUrl)
            ),
            // TODO: because of flowChoise there will be undefined values, remove them ?
            offerDetail,
            simulatorData,
          };

          // TODO: I need to set the address if the flow is complex

          // STEP 2 --> Create/Save LEAD
          createOfferMutation.mutate(offerProps, {
            onSuccess: (result) => {
              console.log(`lead result: ${result}`);
              onSuccess(configId);
            },
            onError: (error: Error) => {
              notifySentry('lead error: ', error);
              toast({
                title: t('common.error.title'),
                description: `${t('common.error.description')}`,
                status: 'error',
                position: 'top',
                duration: 9000,
                isClosable: true,
              });
            },
          });
        },
      }
    );
  };

  const handleB2CSubmit = async (data: FormContactSchema) => {
    tagging.tagAskMeeting();
    submitForm(data);
  };

  const handleB2BSubmit = async (data: FormContactSMESchema) => {
    // TODO: do we need to change the tagging for B2B ?
    tagging.tagAskMeeting();
    submitForm(data);
  };

  // --------------------- effects ------------------------

  useEffect(() => {
    if (apiError) {
      toast({
        title: t('form.submit.error.title'),
        description: `${t('form.submit.error.description')}`,
        status: 'error',
        position: 'top',
        duration: 9000,
        isClosable: true,
      });
    }
  }, [apiError, toast, t]);

  // --------------------- render ------------------------

  return (
    <RoundedBox
      border="solid 3px"
      padding="20px"
      borderRadius="0px 0px 10px 10px"
      // marginTop="50px"
      position="relative"
      top="-20px"
      borderColor={
        promoCodeDetails?.partnerInformation?.backgroundColor ||
        'secondaryColor'
      }
    >
      {/* ----------- PROMOCODE FORM -------------- */}
      {!promoCodeDetails && !preview && (
        <PromoCodeForm onSuccess={setPromoCodeDetails} />
      )}

      {/* ----------- PS -------------- */}
      {!isB2B && numPanels >= 25 && (
        <Text py="20px" color="primaryColor">
          <Trans
            i18nKey="summary.ps1"
            values={{ panel_amount: '30 panneaux' }}
            components={{ bold: <b /> }}
          />
        </Text>
      )}

      {steps.includes(AppRoutes.EXTRA_SERVICES) && <SummaryExtraServices />}

      <HStack paddingBottom="15px">
        <SummaryFormIcon />
        <Text
          ref={formTitleRef}
          fontSize="md"
          fontWeight="bold"
          paddingLeft="5px"
        >
          {isRosselVersion && t('form.title.rossel')}
          {!isRosselVersion && t('form.title')}
        </Text>
      </HStack>

      {
        // --------------------- Contact Forms ------------------------
        !isB2B ? (
          <ContactForm
            isSubmitting={isSubmitting}
            onSubmit={handleB2CSubmit}
            submitElement={
              <Flex flexDir="column" width="100%">
                <Center pt="15px">
                  <Button
                    type="submit"
                    whiteSpace="normal"
                    wordBreak="break-word"
                    isLoading={isSubmitting}
                    width={{ base: '75%', md: '50%' }}
                  >
                    {t('form.contact.submit')}
                  </Button>
                </Center>
              </Flex>
            }
          />
        ) : (
          <SMEContactForm
            isSubmitting={isSubmitting}
            onSubmit={handleB2BSubmit}
            submitElement={
              <Flex
                flexDir={{ sm: 'column', md: 'row' }}
                gap={{ sm: 2, md: 5 }}
                py={5}
                width="100%"
                alignItems="center"
                justifyContent="center"
              >
                <Button
                  type="submit"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  isLoading={isSubmitting}
                >
                  {t('form.contact.sme.submit')}
                </Button>
                <Button
                  type="submit"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  isLoading={isSubmitting}
                  flexDir="column"
                >
                  <Text width="100%">
                    {t('form.contact.sme.submit.v2.line1')}
                  </Text>
                  <Text fontWeight="normal" fontSize="12px">
                    {t('form.contact.sme.submit.v2.line2')}
                  </Text>
                </Button>
              </Flex>
            }
          />
        )
      }
    </RoundedBox>
  );
};

export default SummaryForm;
